<template lang="html">
  <item-editor-dialog
    :id="selectedItemId"
    ref="editForm"
    v-model="selectedPanel"
    :show="showItem"
    :clone-id="cloneId"
    :title="pageTitle"
    :on-close="doClose"
    :context-menu="defaultContextMenu"
    :lock-timer="lockTimerSettings"
    :loading="isDataLoading"
    :errors="errors"
    :edit-panel-id="editPanel"
    :audit-panel-id="auditPanel"
    :audit="auditHistorySettings"
    :buttons="pageBarButtons"
  >
    <!-- START OF FORM FIELDS -->
    <v-flex class="pa-3">
      <v-text-field
        v-model.trim="itemData.name"
        :label="type + ' Name'"
        :rules="rules().required('Name')"
        :error-messages="getPathErrors(jsonPath, 'name')"
        :disabled="isFormDisabled"
        required
        @input="clearPathErrors(jsonPath, 'name')"
      />

      <!-- <v-autocomplete
        v-model="itemData.locationId"
        label="Location"
        placeholder="Select Location"
        :items="locations"
        :rules="rules().required()"
        :required="true"
        :clearable="false"
        :readonly="!isNewItem"
        auto
        :error-messages="getPathErrors(jsonPath, 'locationId')"
        :disabled="isFormDisabled"
        item-text="name"
        item-value="id"
        @input="clearPathErrors(jsonPath, 'locationId')"
      /> -->

      <v-textarea
        v-model.trim="itemData.notes"
        label="Comments"
        :rows="1"
        auto-grow
        :error-messages="getPathErrors(jsonPath, 'notes')"
        :disabled="isFormDisabled"
        @input="clearPathErrors(jsonPath, 'notes')"
      />
      <v-textarea
        v-model.trim="itemData.memberNotice"
        label="Member Notice"
        :rows="1"
        auto-grow
        :error-messages="getPathErrors(jsonPath, 'memberNotice')"
        :disabled="isFormDisabled"
        hint="Message to display to members describing this restriction."
        persistent-hint
        @input="clearPathErrors(jsonPath, 'memberNotice')"
      />
    </v-flex>

    <detail-editor-table
      v-model="itemData.rules"
      type="Rule"
      :json-path="buildJsonPath('rules')"
      :get-path-errors="getPathErrors"
      :clear-path-errors="clearPathErrors"
      :move-path-errors="movePathErrors"
      :disabled="isFormDisabled"
      sortable
      hide-default-footer
      :item-default="{
        id: null,
        type: 'BLOCK',
        memberRestrictionId: null,
        memberTypeId: null,
        membershipTypeId: null,
        memberGroupId: null,
        gender: null,
      }"
      :headers="[
        headerDragSort(),
        headerEditor({label: 'Type', field: 'type', editor: {type: 'slot'}, thin: true}),
        headerEditor({label: 'Gender', field: 'gender', editor: {type: 'slot'}, thin: true}),
        headerEditor({label: 'Member Type', field: 'memberTypeId', editor: {type: 'slot'}}),
        headerEditor({label: 'Membership Type', field: 'membershipTypeId', editor: {type: 'slot'}}),
        headerEditor({label: 'Member Group', field: 'memberGroupId', editor: {type: 'slot'}}),
      ]"
      @error="addError"
    >
      <template
        slot="heading"
      >
        <v-subheader>First matched rule will apply.</v-subheader>
      </template>
      <template
        slot="type"
        slot-scope="props"
      >
        <div style="min-width:150px">
          <v-autocomplete
            v-model="props.item.type"
            placeholder="Type"
            :items="ruleTypes"
            :rules="rules().required()"
            :required="true"
            auto
            :disabled="isFormDisabled"
            :error-messages="props.errorMessages"
            @change="props.errorMessages._clear()"
            @input="props.errorMessages._clear()"
          />
        </div>
      </template>

      <template
        slot="gender"
        slot-scope="props"
      >
        <div style="min-width:150px">
          <v-autocomplete
            v-model="props.item.gender"
            placeholder="All Genders"
            :items="genderSelect"
            auto
            clearable
            :disabled="isFormDisabled"
            :error-messages="props.errorMessages"
            @change="props.errorMessages._clear()"
            @input="props.errorMessages._clear()"
          />
        </div>
      </template>


      <template
        slot="memberTypeId"
        slot-scope="props"
      >
        <v-autocomplete
          v-model="props.item.memberTypeId"
          placeholder="All Member Types"
          :items="memberTypes"
          auto
          clearable
          :disabled="isFormDisabled"
          item-text="name"
          item-value="id"
          :error-messages="props.errorMessages"
          @change="props.errorMessages._clear()"
          @input="props.errorMessages._clear()"
        />
      </template>

      <template
        slot="membershipTypeId"
        slot-scope="props"
      >
        <v-autocomplete
          v-model="props.item.membershipTypeId"
          placeholder="All Membership Types"
          :items="membershipTypes"
          auto
          clearable
          :disabled="isFormDisabled"
          item-text="name"
          item-value="id"
          :error-messages="props.errorMessages"
          @change="props.errorMessages._clear()"
          @input="props.errorMessages._clear()"
        />
      </template>

      <template
        slot="memberGroupId"
        slot-scope="props"
      >
        <v-autocomplete
          v-model="props.item.memberGroupId"
          placeholder="All Member Groups"
          :items="memberGroups.filter(g => !g.staticTypeId)"
          auto
          clearable
          :disabled="isFormDisabled"
          item-text="name"
          item-value="id"
          :error-messages="props.errorMessages"
          @change="props.errorMessages._clear()"
          @input="props.errorMessages._clear()"
        />
      </template>
    </detail-editor-table>

    <v-flex class="pa-3">
      <v-boolean
        v-model="itemData.disabled"
        label="Enabled"
        invert
        :error-messages="getPathErrors(jsonPath, 'disabled')"
        :field-errors="getPathErrors(jsonPath, 'disabled')"
        :disabled="isFormDisabled"
      />
    </v-flex>
    <!-- END OF FORM FIELDS -->
  </item-editor-dialog>
</template>

<script>
import {itemEdit, withChildren, DetailEditorTable} from '@/ft-vue-core'

var itemDefault = {
  id: null,
  name: null,
  notes: null,
  disabled: false,
  rules: []
}

export default {
  name: 'MemberRestrictionItem',
  components: {
    DetailEditorTable
  },
  mixins: [itemEdit, withChildren],
  data: function () {
    return {
      type: 'Member Restriction',
      types: 'Member Restriction',
      apiRecordCommand: 'memberRestriction',
      itemDefault: itemDefault,
      cachedTables: [], // Store previous table settings when switching locations
      memberTypes: [],
      membershipTypes: [],
      memberGroups: [],
      ruleTypes: [],
      genderSelect: [],
      cloner: {
        id: null,
        rules: {
          _cloneMode: Array,
          id: null,
          memberRestrictionId: null,
        }
      }
    }
  },
  computed: {
    memberTypesWithNull () {
      return [{id: null, name: 'Any'}].concat(this.memberTypes)
    },
    membershipTypesWithNull () {
      return [{id: null, name: 'Any'}].concat(this.membershipTypes)
    },
    memberGroupsWithNull () {
      return [{id: null, name: 'Any'}].concat(this.memberGroups)
    },
  },
  watch: {
    // selectedLocationTables () {
    //   this.updateFilteredTables()
    // },
    // selectedItemId () {
    //   this.filteredTablesInit = false
    // }
  },
  methods: {
    itemResourceCommands () {
      return [
        {
          command: 'apiFieldOption',
          load: this.ruleTypes,
          loading: this.isLoading,
          params: {filter_methods: {forField: ['memberRestrictionRule', 'type']}}
        },
        {
          command: 'apiFieldOption',
          load: this.genderSelect,
          loading: this.isLoading,
          params: {filter_methods: {forField: ['memberRestrictionRule', 'gender']}}
        },
        {command: 'memberType', load: this.memberTypes, loading: this.isLoading},
        {command: 'membershipType', load: this.membershipTypes, loading: this.isLoading},
        {command: 'memberGroup', load: this.memberGroups, loading: this.isLoading},
      ]
    },
  }
}
</script>
